import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { GridConfigContext, GRID_SETTING_KEYS, useGridConfig, type GridConfig } from '@/shared/components/Grid';
import type { GridChartSettings } from '@/shared/components/Grid/GridChartPanel';

const STORAGE_KEY = 'numtable.library.view';

interface StoredView {
  hiddenColumns: GridConfig['hiddenColumns'];
  chart: Record<string, GridChartSettings>;
}

function readView(): StoredView {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null');
    if (parsed && typeof parsed === 'object') {
      const view = parsed as Partial<StoredView>;
      return { hiddenColumns: view.hiddenColumns ?? {}, chart: view.chart ?? {} };
    }
  } catch {
    // Blocked or corrupt storage: start from the defaults.
  }
  return { hiddenColumns: {}, chart: {} };
}

/**
 * Grid settings for the Numtable library documents. Everyone is a viewer: no
 * data, format or formula rights, and none of the organization's per-grid
 * layout (order, widths, colors) applies. What a reader may change — which
 * columns show and the chart — is theirs alone, kept in this browser's
 * localStorage and never in `grid_settings`. Number and date formats still
 * come from the surrounding provider.
 */
export function LibraryGridConfig({
  children,
  defaultChart,
}: {
  children: ReactNode;
  /** The chart a reader sees before choosing one, per gridKey. */
  defaultChart: (gridKey: string) => GridChartSettings | undefined;
}) {
  const parent = useGridConfig();
  const [view, setView] = useState<StoredView>(readView);

  const save = useCallback(async (key: string, value: unknown) => {
    if (key !== GRID_SETTING_KEYS.hiddenColumns && key !== GRID_SETTING_KEYS.chart) return null;
    setView((current) => {
      const next = key === GRID_SETTING_KEYS.hiddenColumns
        ? { ...current, hiddenColumns: value as StoredView['hiddenColumns'] }
        : { ...current, chart: value as StoredView['chart'] };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        // Storage unavailable: the choice lasts until the page is left.
      }
      return next;
    });
    return null;
  }, []);

  const value = useMemo<GridConfig>(() => {
    const chart = new Proxy(view.chart, {
      get: (target, gridKey) => (typeof gridKey === 'string' ? target[gridKey] ?? defaultChart(gridKey) : undefined),
    });
    return {
      ...parent,
      documentId: null,
      canEditData: false,
      canEditFormats: false,
      canEditFormulas: false,
      canEditShared: false,
      personalView: true,
      headers: {},
      columnOrder: {},
      rowOrder: {},
      sort: {},
      grouping: {},
      widths: {},
      frozenColumns: {},
      columnDevices: {},
      itemNames: {},
      changeTimeColumns: {},
      headerNotes: {},
      columnGroups: {},
      hiddenAggregates: {},
      aggregateDisplay: {},
      formulas: {},
      summaryCards: {},
      dataFormats: {},
      hiddenColumns: view.hiddenColumns,
      chart,
      save: save as GridConfig['save'],
      reload: () => undefined,
    };
  }, [defaultChart, parent, save, view]);

  return <GridConfigContext.Provider value={value}>{children}</GridConfigContext.Provider>;
}
